/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RegistrationResponse } from "@advapacs/clinician-api-protos/dist/Registration";
import {
  IErrorMessage,
  NestedErrorMessage,
  EulaResponse,
} from "@advapacs/common";

export enum CurrentSignUpStep {
  TYPE = "TYPE",
  CREDENTIAL = "CREDENTIAL",
  PERSONAL_INFORMATION = "PERSONAL_INFORMATION",
  PREFERENCES = "PREFERENCES",
}

const initialState = {
  loading: false,
  individualResponse: null as RegistrationResponse | null,
  organisationResponse: null as RegistrationResponse | null,
  errorMessage: null as IErrorMessage | NestedErrorMessage | null,
  organisationName: "",
  completed: false,
  eulaSignUpAgreement: false,
  showSignUpEulaAgreementInfo: false,
  showLoginEulaAgreementInfo: false,
  eulaDetail: null as EulaResponse | null,
};

const signUpSlice = createSlice({
  name: "signUpSlice",
  initialState,
  reducers: {
    signUpIndividualStart(state) {
      state.loading = true;
    },
    eulaSignUpAgreement(state, action: PayloadAction<boolean>) {
      state.eulaSignUpAgreement = action.payload;
    },
    showSignUpEulaAgreementInfo(state, action: PayloadAction<boolean>) {
      state.showSignUpEulaAgreementInfo = action.payload;
    },
    showLoginEulaAgreementInfo(state, action: PayloadAction<boolean>) {
      state.showLoginEulaAgreementInfo = action.payload;
    },
    signUpIndividualSuccess(
      state,
      action: PayloadAction<RegistrationResponse>
    ) {
      state.individualResponse = action.payload;
      state.loading = false;
      state.errorMessage = null;
    },
    signUpIndividualFail(state, action: PayloadAction<IErrorMessage>) {
      state.errorMessage = action.payload;
      state.loading = false;
    },
    resendEmailStart(state) {
      state.errorMessage = null;
      state.loading = true;
    },
    resendEmailSuccess(state) {
      state.loading = false;
    },
    resendEmailFail(state, action: PayloadAction<IErrorMessage>) {
      state.errorMessage = action.payload;
      state.loading = false;
    },
    reset: () => initialState,
    clearErrorMessage(state) {
      state.errorMessage = null;
    },
    completeStart(state) {
      state.loading = true;
    },
    completeSuccess(state, action) {
      state.loading = false;
      state.completed = true;
      state.organisationName = action.payload.organisationId;
    },
    completeFail(state, action: PayloadAction<IErrorMessage>) {
      state.loading = false;
      state.completed = false;
      state.errorMessage = action.payload;
    },
    signUpOrganisationStart(state) {
      state.loading = true;
    },
    signUpOrganisationSuccess(state, action) {
      state.loading = false;
      state.organisationResponse = action.payload;
      state.errorMessage = null;
    },
    signUpOrganisationFail(state, action: PayloadAction<IErrorMessage>) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
  },
});

export const SignUpAction = signUpSlice.actions;
export default signUpSlice.reducer;
